import React from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  Stack,
} from '@mui/joy'
import { ResponsiveTable } from '../../../components/common/ResponsiveTable'
import Grid from '@mui/joy/Grid'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { Trophy as EmojiEvents, Info } from '@phosphor-icons/react'
import { useSalesReps } from '../../../hooks/useSalesReports'
import type { DateRange, RepMetrics } from '../../../types/salesReports'

interface RepPerformanceProps {
  dateRange: DateRange
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

const getInitials = (name: string) => {
  const parts = name.split(' ')
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
}

interface LeaderboardCardProps {
  title: string
  reps: RepMetrics[]
  valueKey: keyof RepMetrics
  format?: 'currency' | 'percent' | 'number'
}

function LeaderboardCard({ title, reps, valueKey, format = 'number' }: LeaderboardCardProps) {
  const formatValue = (val: number) => {
    switch (format) {
      case 'currency':
        return formatCurrency(val)
      case 'percent':
        return `${val.toFixed(1)}%`
      default:
        return val.toLocaleString()
    }
  }

  const getMedalColor = (index: number) => {
    switch (index) {
      case 0: return '#FFD700' // Gold
      case 1: return '#C0C0C0' // Silver
      case 2: return '#CD7F32' // Bronze
      default: return undefined
    }
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography level="title-sm" startDecorator={<EmojiEvents color="#FFD700" />}>
          {title}
        </Typography>
        <Stack spacing={1} sx={{ mt: 2 }}>
          {reps.slice(0, 5).map((rep, index) => (
            <Stack
              key={rep.user_id}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                p: 1,
                borderRadius: 'sm',
                bgcolor: index === 0 ? 'warning.softBg' : undefined
              }}
            >
              <Typography
                level="body-sm"
                sx={{
                  width: 20,
                  fontWeight: 'lg',
                  color: getMedalColor(index)
                }}
              >
                {index + 1}
              </Typography>
              <Avatar size="sm" sx={{ bgcolor: getMedalColor(index) }}>
                {getInitials(rep.name)}
              </Avatar>
              <Typography level="body-sm" sx={{ flex: 1 }}>
                {rep.name}
              </Typography>
              <Typography level="body-sm" fontWeight="lg">
                {formatValue(rep[valueKey] as number)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  )
}

export default function RepPerformance({ dateRange }: RepPerformanceProps) {
  const { data, isLoading, error } = useSalesReps({
    date_from: dateRange.from,
    date_to: dateRange.to,
  })

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert color="danger">
        Failed to load rep performance data. Please try again.
      </Alert>
    )
  }

  if (!data) {
    return (
      <Alert color="warning">
        No data available for the selected period.
      </Alert>
    )
  }

  const { reps, team_totals, leaderboards } = data

  // Chart data for comparison
  const chartData = reps.slice(0, 10).map(rep => ({
    name: rep.name.split(' ')[0], // First name only for chart
    pipeline: rep.pipeline_value,
    won: rep.won_value,
    revenue: rep.job_revenue,
  }))

  return (
    <Box>
      <Typography level="title-lg" sx={{ mb: 3 }}>
        Sales Rep Performance
      </Typography>

      {/* Team Totals */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid xs={6} sm={4} md={2}>
          <Card variant="soft" color="primary">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Total Pipeline</Typography>
              <Typography level="h4">{formatCurrency(team_totals.total_pipeline)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={4} md={2}>
          <Card variant="soft" color="success">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Total Won</Typography>
              <Typography level="h4">{formatCurrency(team_totals.total_won_value)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={4} md={2}>
          <Card variant="soft" color="warning">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Job Revenue</Typography>
              <Typography level="h4">{formatCurrency(team_totals.total_job_revenue)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={4} md={2}>
          <Card variant="soft" color="neutral">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Commission</Typography>
              <Typography level="h4">{formatCurrency(team_totals.total_commission)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={4} md={2}>
          <Card variant="soft">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Opps Created</Typography>
              <Typography level="h4">{team_totals.total_opps_created}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={4} md={2}>
          <Card variant="soft" color="success">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Opps Won</Typography>
              <Typography level="h4">{team_totals.total_opps_won}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Leaderboards */}
      <Typography level="title-md" sx={{ mb: 2, color: 'text.secondary' }}>
        Leaderboards
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid xs={12} sm={6} lg={3}>
          <LeaderboardCard
            title="By Revenue"
            reps={leaderboards.by_revenue}
            valueKey="job_revenue"
            format="currency"
          />
        </Grid>
        <Grid xs={12} sm={6} lg={3}>
          <LeaderboardCard
            title="By Won Value"
            reps={leaderboards.by_won_value}
            valueKey="won_value"
            format="currency"
          />
        </Grid>
        <Grid xs={12} sm={6} lg={3}>
          <LeaderboardCard
            title="By Win Rate"
            reps={leaderboards.by_win_rate}
            valueKey="win_rate"
            format="percent"
          />
        </Grid>
        <Grid xs={12} sm={6} lg={3}>
          <LeaderboardCard
            title="By Activities"
            reps={leaderboards.by_activities}
            valueKey="activities_logged"
            format="number"
          />
        </Grid>
      </Grid>

      {/* Performance Chart */}
      {chartData.length > 0 && (
        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Rep Comparison
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(val) => `$${(val / 1000).toFixed(0)}k`} />
                <Tooltip
                  formatter={(value) => formatCurrency(value as number)}
                  itemSorter={(item) => {
                    const order = ['pipeline', 'won', 'revenue']
                    return order.indexOf(item.dataKey as string)
                  }}
                />
                <Legend />
                <Bar dataKey="pipeline" fill="#0B6BCB" name="Pipeline" />
                <Bar dataKey="won" fill="#06A77D" name="Won" />
                <Bar dataKey="revenue" fill="#F9A825" name="Revenue" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Detailed Table */}
      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Typography level="title-md" sx={{ mb: 2 }}>
            Detailed Performance
          </Typography>
          <ResponsiveTable<RepMetrics>
            rows={reps}
            getRowKey={(rep) => rep.user_id}
            columns={[
              {
                key: 'rep',
                sortValue: (row) => row.name,
                header: 'Rep',
                mobileVariant: 'header',
                render: (rep) => (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar size="sm">{getInitials(rep.name)}</Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography level="body-sm" fontWeight="lg">{rep.name}</Typography>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary', wordBreak: 'break-all' }}>
                        {rep.email}
                      </Typography>
                    </Box>
                  </Stack>
                ),
              },
              { key: 'opportunities_created', sortValue: (row) => row.opportunities_created, header: 'Opps Created', render: (r) => <Typography level="body-sm">{r.opportunities_created}</Typography> },
              { key: 'opportunities_open', sortValue: (row) => row.opportunities_open, header: 'Open', render: (r) => <Typography level="body-sm">{r.opportunities_open}</Typography> },
              { key: 'opportunities_won', sortValue: (row) => row.opportunities_won, header: 'Won', render: (r) => <Typography level="body-sm">{r.opportunities_won}</Typography> },
              {
                key: 'win_rate',
                header: 'Win Rate',
                render: (rep) => (
                  <Chip
                    size="sm"
                    variant="soft"
                    color={rep.win_rate >= 50 ? 'success' : rep.win_rate >= 25 ? 'warning' : 'neutral'}
                  >
                    {rep.win_rate}%
                  </Chip>
                ),
              },
              { key: 'pipeline_value', header: 'Pipeline', render: (r) => <Typography level="body-sm">{formatCurrency(r.pipeline_value)}</Typography> },
              { key: 'won_value', header: 'Won Value', render: (r) => <Typography level="body-sm">{formatCurrency(r.won_value)}</Typography> },
              { key: 'job_revenue', header: 'Job Revenue', render: (r) => <Typography level="body-sm">{formatCurrency(r.job_revenue)}</Typography> },
              { key: 'commission_earned', header: 'Commission', render: (r) => <Typography level="body-sm">{formatCurrency(r.commission_earned)}</Typography> },
              { key: 'avg_sales_cycle', header: 'Avg Cycle', render: (r) => <Typography level="body-sm">{r.avg_sales_cycle} days</Typography> },
              { key: 'calls_made', header: 'Calls placed', render: (r) => <Typography level="body-sm">{r.calls_made ?? '—'}</Typography> },
              { key: 'emails_sent', header: 'Emails accepted', render: (r) => <Typography level="body-sm">{r.emails_sent ?? '—'}</Typography> },
              { key: 'meetings_held', header: 'Meetings held', render: (r) => <Typography level="body-sm">{r.meetings_held ?? '—'}</Typography> },
              { key: 'quota', header: 'Won-value target', render: (r) => <Typography level="body-sm">{r.quota === null ? 'Not configured' : formatCurrency(r.quota)}{r.quota_progress === null ? '' : ` (${r.quota_progress}%)`}</Typography> },
              { key: 'activities_logged', header: 'Activities', render: (r) => <Typography level="body-sm">{r.activities_logged}</Typography> },
            ]}
          />
        </CardContent>
      </Card>

      {/* Metric definitions */}
      <Alert
        color="neutral"
        startDecorator={<Info />}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography level="title-sm">Activity and target definitions</Typography>
          <Typography level="body-sm">Activity comes from deduplicated Inbox records and completed meetings for contacts you can access. Email acceptance is not proof of delivery; manual notes stay separate.</Typography>
          <Typography level="body-sm">Configure monthly won-value targets in Sales Desk. Targets are prorated by calendar day for this period.</Typography>
        </Box>
      </Alert>
    </Box>
  )
}
